/**
 * Task Completion Utilities
 * Handles task completion criteria checking and tracking
 */

/**
 * Check if task completion criteria are met
 * Criteria: User has chatted with relevant stakeholders AND uploaded required artifact (if specified)
 */
export function checkTaskCompletionCriteria(taskIndex, scenarioId, task, conversationHistory, artifacts) {
  // Check if user has chatted with stakeholders
  const hasChatted = conversationHistory && Object.keys(conversationHistory).length > 0;
  
  // Check if required artifact exists
  const requiredArtifact = task?.requiredArtifact;
  
  // If no artifact is required, task doesn't auto-complete (user must manually progress)
  // Only tasks with required artifacts can auto-complete
  if (!requiredArtifact) {
    return false; // Don't auto-complete tasks without artifact requirements
  }
  
  // Check if user has uploaded the required artifact for this task
  const taskArtifacts = artifacts.filter(artifact => 
    artifact.scenarioId === scenarioId && 
    (artifact.taskIndex === taskIndex || artifact.taskIndex === null)
  );

  if (taskArtifacts.length === 0) {
    return false;
  }

  // Check if any artifact matches the required type (by name containing the required type)
  const hasRequiredArtifact = taskArtifacts.some(artifact => {
    const fileName = artifact.fileName.toLowerCase();
    const requiredType = requiredArtifact.toLowerCase();
    // Match if filename contains required type keywords
    const requiredKeywords = requiredType.split(' ').filter(k => k.length > 2);
    return requiredKeywords.some(keyword => fileName.includes(keyword)) ||
           fileName.split('.')[0].includes(requiredType.split(' ')[0]);
  });

  return hasChatted && hasRequiredArtifact;
}

/**
 * Get completed task indices for a scenario
 */
export function getCompletedTasks(scenarioId, taskCompletions = {}) {
  return taskCompletions[scenarioId] || [];
}

/**
 * Check if a specific task is completed
 */
export function isTaskCompleted(scenarioId, taskIndex, taskCompletions = {}) {
  const completed = getCompletedTasks(scenarioId, taskCompletions);
  return completed.includes(taskIndex);
}

/**
 * Mark a task as completed
 */
export function markTaskComplete(scenarioId, taskIndex, taskCompletions = {}) {
  const completed = getCompletedTasks(scenarioId, taskCompletions);
  if (!completed.includes(taskIndex)) {
    return {
      ...taskCompletions,
      [scenarioId]: [...completed, taskIndex].sort((a, b) => a - b)
    };
  }
  return taskCompletions;
}

/**
 * Check if all tasks for a scenario are completed
 */
export function areAllTasksCompleted(scenarioId, scenarioTasks, taskCompletions = {}) {
  const completed = getCompletedTasks(scenarioId, taskCompletions);
  return scenarioTasks && completed.length === scenarioTasks.length;
}

